import { SyntheticEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { isArray, isEqual } from "lodash"

import Selector from "@/components/form/selector/Selector"
import BasicButton from "@/components/button/BasicButton"
import { createGlobalStore } from "@/stores/global"
import { useUnitQuery, useUnitsQuery, useUpsertUnitMutation } from "@/hooks/api/useSettingQuery"
import { Unit } from "@/types/setting"
import { SquareUnit, UnitType } from "@/constants/unit"
import { useNativeBridge } from "@/hooks/useNativeBridge"

export default function UnitSelectors() {
  const { t } = useTranslation()
  const [unitState, setUnitState] = useState<{ [key: string]: Unit }>({})
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const { notifySettingChanged } = useNativeBridge()

  const { data: units } = useUnitsQuery()
  const { data: unit } = useUnitQuery()
  const { mutateAsync } = useUpsertUnitMutation()

  useEffect(() => {
    if (unit) {
      unit.forEach((u) => {
        setUnitState((prev) => ({
          ...prev,
          [u.type]: u,
        }))
      })
    }
  }, [unit])

  const getUnitOptions = (type: UnitType) => {
    return units?.filter((unit) => unit.type === type) ?? []
  }

  const setSelectorValue = (type: UnitType, value: Unit) => {
    setUnitState({
      ...unitState,
      [type]: value,
    })
  }

  const isErr = () => {
    const isEmpty = Object.values(unitState).some((unit) => !unit)
    const sortByType = (a: Unit, b: Unit) => a.type.localeCompare(b.type)
    const currentSorted = Object.values(unitState).sort(sortByType)
    const originalSorted = unit ? [...unit].sort(sortByType) : []

    return isEqual(currentSorted, originalSorted) || isEmpty
  }

  const onSave = async () => {
    notifySettingChanged("unit", "saving")
    try {
      await mutateAsync(
        Object.values(unitState).map((unit) => ({
          idfUnit: unit.idfUnit,
          unit: unit.unit,
          type: unit.type,
        })),
      )
      setSnackBar({
        open: true,
        message: t("messages.successUpdate"),
        severity: "success",
      })
      notifySettingChanged("unit", "update")
    } catch (e) {
      notifySettingChanged("unit", "error")

      return e
    }
  }

  const UnitRow = ({
    title,
    dataKey,
    isFlex = false,
  }: {
    title: string | string[]
    dataKey: string | string[]
    isFlex?: boolean
  }) => {
    if (isFlex && isArray(title) && isArray(dataKey)) {
      return (
        <div className="FlexRowCont">
          {title.map((t, i) => (
            <div key={t}>
              <p className="row-title">{t}</p>
              <SelectorWrapper
                value={unitState?.[dataKey[i]]}
                options={getUnitOptions(dataKey[i] as UnitType)}
                onChange={(_, value) => setSelectorValue(dataKey[i] as UnitType, value)}
                isValueSquare={dataKey[i] === UnitType.ACCELERATION}
              />
            </div>
          ))}
        </div>
      )
    } else {
      return (
        <div className="RowCont">
          <p className="row-title">{title}</p>
          <SelectorWrapper
            value={unitState?.[dataKey as UnitType]}
            options={getUnitOptions(dataKey as UnitType)}
            onChange={(_, value) => setSelectorValue(dataKey as UnitType, value)}
          />
        </div>
      )
    }
  }

  const rowArray = [
    {
      title: [t("distance"), t("distanceMin")],
      key: [UnitType.DISTANCE, UnitType.DISTANCE_MIN],
      isFlex: true,
    },
    {
      title: t("actionDistance"),
      key: UnitType.ACTION_DISTANCE,
    },
    {
      title: [t("speed"), t("acceleration")],
      key: [UnitType.SPEED, UnitType.ACCELERATION],
      isFlex: true,
    },
    {
      title: t("power"),
      key: UnitType.POWER,
    },
    {
      title: t("heartRate"),
      key: UnitType.HR,
    },
  ]

  return (
    <>
      <div className="UnitListCont">
        {rowArray.map((row, i) => (
          <UnitRow key={i} title={row.title} dataKey={row.key} isFlex={row.isFlex} />
        ))}
      </div>
      <div className="ButtonCont">
        <BasicButton type="primary-contained" size="lg" onClick={onSave} disabled={isErr()}>
          {t("button.save")}
        </BasicButton>
      </div>
    </>
  )
}

const SelectorWrapper = ({
  onChange,
  value,
  options,
  isValueSquare = false,
}: {
  onChange: (event: SyntheticEvent, value: Unit, reason?: string, details?: string) => void
  value?: Unit
  options?: Unit[]
  isValueSquare?: boolean
}) => {
  const [originalValue, setOriginalValue] = useState<Unit | undefined>()

  useEffect(() => {
    if (!originalValue) {
      setOriginalValue(value)
    }
  }, [value])

  const formatValue = () => {
    if (!value) {
      return ""
    }

    return {
      ...value,
      unit: isValueSquare ? SquareUnit[value.unit] : value.unit,
    }
  }

  return (
    // power, hr 제외 나머지는 options 의 첫번째 값이 default 값
    <Selector
      value={formatValue()}
      options={options ?? []}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props

        return (
          <span key={option.idfUnit} {...optionProps}>
            {isValueSquare ? SquareUnit[option.unit] : option.unit}
          </span>
        )
      }}
      onChange={onChange}
      labelKey="unit"
      onBlur={(e) => {
        // 값을 지운 후 onBlur 시 그대로 빈값으로 들어가는 문제 해결
        if (!formatValue() && originalValue) {
          onChange(e, originalValue)
        }
      }}
    />
  )
}
